import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  Component,
  DestroyRef,
  Inject,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgModel } from '@angular/forms';
import { take } from 'rxjs';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import {
  PackageHistory,
  PackageInquiry,
} from 'src/app/core/models/package-tracking.model';
import { DataService } from 'src/app/core/services/data/data.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { IMAGE_SRC } from '../../shared/shared-constants';
import {
  CONSTS,
  PACKAGE_TABS,
  PackageTrackingDataType,
} from './package-tracking.constants';

@Component({
  selector: 'app-package-tracking-popup',
  templateUrl: './package-tracking-popup.component.html',
  styleUrls: ['./package-tracking-popup.component.scss'],
  standalone: false,
})
export class PackageTrackingPopupComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);

  readonly tabs = PACKAGE_TABS;
  readonly IMAGES = IMAGE_SRC;
  readonly CONSTANTS = CONSTS;

  tab = PACKAGE_TABS.INQUIRY;
  searchQuery = '';
  details?: PackageInquiry;
  history?: PackageHistory;
  currentUser!: UserAdmin;

  @ViewChild('searchModel') searchModel!: NgModel;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PackageTrackingDataType,
    public dialogRef: MatDialogRef<PackageTrackingPopupComponent>,
    public commonUtils: CommonUtils,
    private readonly dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.searchModel?.valueChanges
      ?.pipe(takeUntilDestroyed(this.destroy))
      .subscribe((val) => {
        const next = (val ?? '').toString().toUpperCase();
        if (next !== val) {
          this.searchModel.control.setValue(next, { emitEvent: false });
        }
      });

    this.userService
      .getCurrentUser$()
      .pipe(take(1))
      .subscribe((value) => {
        this.currentUser = value;
        this.searchQuery = this.data.searchQuery || '';
        this.details = {
          originDetails: this.data.originDetails,
          destDetails: this.data.destinationDetails,
          accessorials: this.data.accessorials,
          hazmatAccessorials: this.data.hazmatAccessorials,
          latestTracknum: this.data.latestTracknum,
        };
      });
  }

  updateTab($event: CustomEvent) {
    this.tab = $event.detail.value;
    if (this.history === undefined && this.tab === PACKAGE_TABS.HISTORY) {
      this.getHistory();
    }
    if (this.details === undefined && this.tab === PACKAGE_TABS.INQUIRY) {
      this.getDetails();
    }
  }

  clearSearch() {
    this.searchQuery = '';
  }

  keyPress($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      if (this.searchQuery.length === 0) {
        return;
      } else {
        if (this.tab === PACKAGE_TABS.INQUIRY) {
          this.history = undefined;
          this.details = undefined;
          this.getDetails();
        }

        if (this.tab === PACKAGE_TABS.HISTORY) {
          this.history = undefined;
          this.details = undefined;
          this.getHistory();
        }
      }
    } else {
      this.commonUtils.preventSpecialCharacters(
        $event,
        SpecialCharacters.ALPHABET_NUMBER_NO_SPACE,
      );
    }
  }

  private getDetails() {
    this.dataService
      .getPackageDetailsById(
        this.searchQuery.toUpperCase(),
        this.currentUser.location,
      )
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((res) => {
        this.details = {
          originDetails: res?.response.packageInquiry[0].originDetails,
          destDetails: res?.response.packageInquiry[0].destDetails,
          accessorials: res?.response.packageInquiry[0].accessorials,
          hazmatAccessorials:
            res?.response.packageInquiry[0].hazmatAccessorials,
          latestTracknum: res?.response.packageInquiry[0].latestTracknum,
        };
        this.commonUtils.updateLoadingModal(false);
      });
  }

  private getHistory() {
    this.dataService
      .getPackageHistory(
        this.searchQuery.toUpperCase(),
        this.currentUser.location,
      )
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((res) => {
        this.history = res?.response?.packageHistoryEvents[0];
        this.commonUtils.updateLoadingModal(false);
      });
  }
}
